import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { en } from '../content/en';
import { ptBR, type Dictionary } from '../content/pt-BR';
import { setDictionary } from './dict';
import { setFormatLocale } from './format';

/**
 * Idioma da interface.
 *
 * ── Por que isto existe ──
 * A Meta exige que o screencast do App Review mostre a interface **em inglês**
 * ✅ (*"set the app's user interface language to English before recording"*), ou
 * legendas explicando cada passo. Traduzir o produto é mais honesto que legendar
 * um vídeo — e resolve de vez, em vez de a cada gravação.
 *
 * O efeito colateral é o produto: a atendente brasileira continua em pt-BR, que
 * é o padrão, e o inglês passa a existir para quem precisar dele.
 *
 * ── A escolha de arquitetura ──
 * Dicionário tipado, sem biblioteca. `Dictionary` é derivado do português, então
 * **o inglês não compila se faltar uma chave** — que é a única garantia que
 * importa numa tradução: a de que nada ficou para trás. Uma lib de i18n daria
 * pluralização e carregamento sob demanda; nenhum dos dois faz falta em duas
 * línguas e num app que já carrega tudo de uma vez.
 */

export const locales = ['pt-BR', 'en'] as const;
export type Locale = (typeof locales)[number];

export const localeLabels: Record<Locale, string> = {
  'pt-BR': 'PT',
  en: 'EN',
};

/**
 * O nome de cada idioma NO PRÓPRIO idioma — e por isso não passa pelo dicionário.
 * "Portuguese" escrito para quem só lê português não ajuda ninguém a se achar.
 */
export const localeNames: Record<Locale, string> = {
  'pt-BR': 'Português (Brasil)',
  en: 'English',
};

/** Tags BCP-47, para `Intl` e para o atributo `lang` do documento. */
const localeTags: Record<Locale, string> = {
  'pt-BR': 'pt-BR',
  en: 'en-US',
};

const dictionaries: Record<Locale, Dictionary> = { 'pt-BR': ptBR, en };

const STORAGE_KEY = 'zenda.locale';

function isLocale(value: string | null): value is Locale {
  return value !== null && (locales as readonly string[]).includes(value);
}

/**
 * Idioma inicial: escolha explícita anterior, senão o do navegador, senão pt-BR.
 *
 * O padrão é português porque o produto é brasileiro — quem o usa atende
 * pacientes no Brasil. O inglês é a exceção, não o meio-termo.
 */
function detectLocale(): Locale {
  const salvo = window.localStorage.getItem(STORAGE_KEY);
  if (isLocale(salvo)) return salvo;

  for (const tag of window.navigator.languages ?? []) {
    const base = tag.toLowerCase().split('-')[0];
    if (base === 'pt') return 'pt-BR';
    if (base === 'en') return 'en';
  }
  return 'pt-BR';
}

interface I18nValue {
  readonly locale: Locale;
  readonly t: Dictionary;
  readonly setLocale: (next: Locale) => void;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const inicial = detectLocale();
    // Antes do primeiro render: `format.ts` e `dict.ts` são chamados de fora do
    // React, e um primeiro quadro com data no formato errado é visível.
    setFormatLocale(localeTags[inicial]);
    setDictionary(dictionaries[inicial]);
    document.documentElement.lang = localeTags[inicial];
    return inicial;
  });

  const setLocale = useCallback((next: Locale) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    // `lang` no documento não é detalhe de acessibilidade apenas: é o que faz o
    // leitor de tela pronunciar o texto na língua certa, e o que o navegador
    // usa para hifenização e correção ortográfica dentro dos campos.
    document.documentElement.lang = localeTags[next];
    setFormatLocale(localeTags[next]);
    setDictionary(dictionaries[next]);
    setLocaleState(next);
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ locale, t: dictionaries[locale], setLocale }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) throw new Error('useI18n fora de <I18nProvider>');
  return ctx;
}

/** Atalho para quem só quer o texto: `const t = useT();` */
export function useT(): Dictionary {
  return useI18n().t;
}
